import * as React from 'react'

import Theme from '@theme/Theme'
import styled from '@theme/styled-components'
import useI18n from '@store/i18n/useI18n'
import Loader from 'react-loader-spinner'
import InfiniteList from '@components/infiniteList/infiniteList'
import Chantier from '@components/listItem/business'
import BusinessFilters from '@components/filter/businessFilters'
import PaginationComponent from '@components/pagination/PaginationComponent'

const BUSINESS_HEIGHT = 150

interface Props {
  navigation: Navigation
  screenStatus: ScreenStatus
  setScreenStatus: (s: ScreenStatus) => void
  businesses: BusinessResume[]
  setBusinesses: (b: BusinessResume[]) => void
  setClusters: (clusters: Cluster[]) => void
  setIsolatedBusinesses: (isolatedBusinesses: BusinessLocation[]) => void
  setOrphans: (o: OrphanDeclarationResume[]) => void
  orphansVisible: boolean
  setOrphansVisible: (v: boolean) => void
  orphansCount: number
  mapBusinessesCount: number
}

const BusinessesMenu = ({
  navigation,
  screenStatus,
  setScreenStatus,
  businesses,
  setBusinesses,
  setClusters,
  setIsolatedBusinesses,
  setOrphans,
  orphansVisible,
  setOrphansVisible,
  orphansCount,
}: Props) => {
  const i18n = useI18n()

  //Etat du menu de filtrage
  const [menuState, setMenuState] = React.useState<'opened' | 'closed'>('closed')
  const [numberBusinesses, setNumberBusinesses] = React.useState<number>()
  const [currentPage, setCurrentPage] = React.useState(1)

  return (
    <Container>
      <BusinessFilters
        setBusinesses={setBusinesses}
        setClusters={setClusters}
        setIsolatedBusinesses={setIsolatedBusinesses}
        setOrphans={setOrphans}
        screenStatus={screenStatus}
        setScreenStatus={setScreenStatus}
        orphansVisible={orphansVisible}
        setOrphansVisible={setOrphansVisible}
        menuState={menuState}
        setMenuState={setMenuState}
        orphansCount={orphansCount}
        setNumberBusinesses={setNumberBusinesses}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
      {screenStatus !== 'fetched' ? (
        screenStatus === 'loading' ? (
          <StatusContainer>
            <Loader type="ThreeDots" color={Theme.colors.classicButtonLoader} height={30} />
          </StatusContainer>
        ) : (
          <ErrorLibelle>{i18n.t('components.detailsChantier.error')}</ErrorLibelle>
        )
      ) : (
        <>
          {!!numberBusinesses && (
            <TitleContainer>
              <Title>{i18n.t('components.menu.listTitle', { count: numberBusinesses })}</Title>
            </TitleContainer>
          )}
          <ListContainer minHeight={BUSINESS_HEIGHT}>
            {businesses.length > 0 ? (
              <>
                <InfiniteList
                  key={menuState}
                  data={businesses}
                  height={BUSINESS_HEIGHT}
                  renderItem={(chantier) => (
                    <Chantier
                      chantier={chantier}
                      height={150}
                      onClick={() => navigation.push(`/focus/business/${chantier.id}`)}
                    />
                  )}
                />
                {!!numberBusinesses && (
                  <PaginationComponent
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    numberBusinesses={numberBusinesses}
                  />
                )}
              </>
            ) : (
              <NoChantierLibelle>{i18n.t('components.menu.noChantier')}</NoChantierLibelle>
            )}
          </ListContainer>
        </>
      )}
    </Container>
  )
}

export default BusinessesMenu

const Container = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;

  padding: 20px 10px;
  padding-bottom: 0;

  overflow: hidden;
`
const Title = styled('div')`
  ${(props) => props.theme.fonts.title};
`
const TitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  border-top-width: 1px;
  padding: 15px 0;

  display: flex;
  justify-content: space-between;

  flex-direction: column;
  align-items: start;
`
const ListContainer = styled('div')<{ minHeight: number }>`
  flex-direction: column;
  overflow-y: scroll;
  margin: 0 -10px;
  display: flex;
  flex: 1;
  min-height: ${(props) => props.minHeight}px;
`
const NoChantierLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
  margin: 40px;
`
const StatusContainer = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`
const ErrorLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
  margin: 40px;
`
